import type { AuthState, UserStatus } from '../../types';

import { onAuthStateChanged } from 'firebase/auth';
import { useSetState } from 'minimal-shared/hooks';
import { UserStatus as SharedUserStatus } from '@artham/shared';
import { useRef, useMemo, useState, useEffect, useCallback } from 'react';

import { useFCM } from 'src/hooks/use-fcm';

import { isNativePlatform } from 'src/utils/capacitor';
import { handleNotificationTap } from 'src/utils/notification-router';

import { AUTH } from 'src/lib/firebase';
import axios, { endpoints, setAuthToken, getJwtExpirationMs } from 'src/lib/axios-artham';

import { NotificationHandler } from 'src/components/fcm/notification-handler';

import { AuthContext } from '../auth-context';
import { signOut, nativeGetIdToken, nativeGetCurrentUser } from './action';

// ----------------------------------------------------------------------

const LAST_LOGIN_KEY = 'auth_last_login_timestamp';
const LOGIN_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

const setLastLoginTimestamp = () => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(LAST_LOGIN_KEY, Date.now().toString());
  }
};

const getLastLoginTimestamp = (): number | null => {
  if (typeof window !== 'undefined') {
    const timestamp = localStorage.getItem(LAST_LOGIN_KEY);
    return timestamp ? parseInt(timestamp, 10) : null;
  }
  return null;
};

const clearLastLoginTimestamp = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(LAST_LOGIN_KEY);
  }
};

// Format an unknown error (axios | Error | anything) into a single short string for logging.
const describeError = (error: unknown): string => {
  const e = error as {
    response?: { status?: number; data?: { code?: string; message?: string } };
    message?: string;
    code?: string;
  };
  const status = e.response?.status;
  const body = e.response?.data;
  return [status, body?.code, body?.message ?? e.message ?? e.code ?? 'unknown error']
    .filter(Boolean)
    .join(' ');
};

const isLoginExpired = (): boolean => {
  const lastLogin = getLastLoginTimestamp();
  if (!lastLogin) return false;

  const now = Date.now();
  const timeSinceLogin = now - lastLogin;
  return timeSinceLogin >= LOGIN_EXPIRY_MS;
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({ user: null, loading: true, error: null });
  const [needsProfile, setNeedsProfile] = useState(false);
  const [userStatus, setUserStatus] = useState<UserStatus>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  // FCM Push Notifications
  const notificationHandler = NotificationHandler();
  const fcm = useFCM({
    onNotificationReceived: notificationHandler.handleNotification,
    onNotificationActionPerformed: (notification: any) => {
      handleNotificationTap(notification, (path: string) => {
        window.location.href = path;
      });
    },
  });
  const fcmInitializedRef = useRef(false);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await axios.get(endpoints.auth.me);

      // Check response code
      if (response?.data?.code === SharedUserStatus.UNKNOWN_USER) {
        setNeedsProfile(true);
        setUserStatus(SharedUserStatus.UNKNOWN_USER);
        setUserRole('DEALER');
        return null;
      }

      setNeedsProfile(false);
      setUserStatus(response?.data?.code); // KYC_PENDING, ACTIVE, DISABLED
      setUserRole(response?.data?.user?.role || 'DEALER');
      return response.data;
    } catch (error) {
      const code = (error as { response?: { data?: { code?: string } } }).response?.data?.code;

      // UNKNOWN_USER returned via error envelope - expected for first-time sign-in
      if (code === SharedUserStatus.UNKNOWN_USER) {
        setNeedsProfile(true);
        setUserStatus(SharedUserStatus.UNKNOWN_USER);
        setUserRole('DEALER');
        return null;
      }

      console.warn('[auth] fetchUserProfile failed:', describeError(error));
      return null;
    }
  }, []);

  const handleAuthenticatedUser = useCallback(
    async (user: AuthState['user']) => {
      if (!user) {
        clearLastLoginTimestamp();
        setState({ user: null, loading: false, error: null });
        setNeedsProfile(false);
        setUserStatus(null);
        setUserRole(null);
        setAuthToken(null);
        return;
      }

      // Check if login has expired (24 hours have passed)
      if (isLoginExpired()) {
        console.log('Login expired after 24 hours, logging out user');
        await signOut();
        clearLastLoginTimestamp();
        setState({ user: null, loading: false, error: null });
        setNeedsProfile(false);
        setUserStatus(null);
        setUserRole(null);
        setAuthToken(null);
        return;
      }

      try {
        // Set login timestamp for new logins
        setLastLoginTimestamp();

        // Setup token management
        await setAuthToken(user);

        // Fetch user profile from API
        const profileResponse = await fetchUserProfile();

        setState({
          user: {
            ...user,
            profileData: profileResponse?.user || null,
            status: profileResponse?.code || SharedUserStatus.UNKNOWN_USER,
          },
          loading: false,
          error: null,
        });
      } catch (error) {
        console.warn('[auth] setup failed:', describeError(error));
        setState({
          user: {
            ...user,
            profileData: null,
            status: SharedUserStatus.UNKNOWN_USER,
          },
          loading: false,
          error: error instanceof Error ? error : new Error('Failed to fetch user data'),
        });
      }
    },
    [setState, fetchUserProfile]
  );

  const checkUserSession = useCallback(async () => {
    try {
      if (isNativePlatform()) {
        // Native: Check current user via Capacitor plugin with timeout fallback
        const nativeAuthCheck = async () => {
          try {
            const nativeUser = await nativeGetCurrentUser();
            if (nativeUser) {
              const idToken = await nativeGetIdToken();
              // Build a user-like object compatible with Firebase web SDK user shape
              const userLike = {
                uid: nativeUser.uid,
                email: nativeUser.email,
                phoneNumber: nativeUser.phoneNumber,
                displayName: nativeUser.displayName,
                photoURL: nativeUser.photoUrl,
                accessToken: idToken,
                // Delegate to the native plugin so the token can actually be
                // refreshed: getIdToken(false) returns the plugin's current
                // (auto-refreshed) token, getIdToken(true) forces a fresh fetch.
                // Returning the captured `idToken` here meant native sessions
                // could never refresh and went 401 once it expired (~1h).
                getIdToken: async (forceRefresh?: boolean) => nativeGetIdToken(!!forceRefresh),
                getIdTokenResult: async () => ({
                  token: idToken,
                  expirationTime: new Date(getJwtExpirationMs(idToken)).toISOString(),
                  authTime: '',
                  issuedAtTime: '',
                  signInProvider: null,
                  signInSecondFactor: null,
                  claims: {},
                }),
              } as AuthState['user'];
              await handleAuthenticatedUser(userLike);
            } else {
              await handleAuthenticatedUser(null);
            }
          } catch (error) {
            console.warn('[auth] native check failed:', describeError(error));
            await handleAuthenticatedUser(null);
          }
        };

        // Race against a 5-second timeout
        await Promise.race([
          nativeAuthCheck(),
          new Promise<void>((resolve) => {
            setTimeout(() => {
              console.warn('Native auth check timed out after 5s');
              setState({ loading: false });
              resolve();
            }, 5000);
          }),
        ]);

        // Also listen for auth state changes (token refreshes, etc.)
        onAuthStateChanged(AUTH, async (user: AuthState['user']) => {
          if (user) {
            await handleAuthenticatedUser(user);
          }
        });
      } else {
        // Web: Use Firebase onAuthStateChanged as before
        onAuthStateChanged(AUTH, async (user: AuthState['user']) => {
          await handleAuthenticatedUser(user);
        });
      }
    } catch (error) {
      console.warn('[auth] checkUserSession failed:', describeError(error));
      clearLastLoginTimestamp();
      setState({
        user: null,
        loading: false,
        error: error instanceof Error ? error : new Error('Authentication failed'),
      });
      setNeedsProfile(false);
      setAuthToken(null);
    }
  }, [setState, handleAuthenticatedUser]);

  const createUserProfile = useCallback(
    async (profileData: any) => {
      try {
        const response = await axios.post(endpoints.auth.me, profileData);

        setState({
          user: {
            ...state.user,
            profileData: response.data?.user || null,
            status: response.data?.code || SharedUserStatus.KYC_PENDING,
          },
        });

        setNeedsProfile(false);
        setUserStatus(response.data?.code);
        setUserRole(response.data?.user?.role || 'DEALER');

        return response.data;
      } catch (error) {
        console.warn('[auth] createUserProfile failed:', describeError(error));
        throw error;
      }
    },
    [state.user, setState]
  );

  const refreshProfile = useCallback(async () => {
    if (!state.user) {
      return;
    }

    try {
      const profileResponse = await fetchUserProfile();

      if (profileResponse) {
        setState({
          user: {
            ...state.user,
            profileData: profileResponse.user || null,
            status: profileResponse.code || SharedUserStatus.UNKNOWN_USER,
          },
        });
      }
    } catch (error) {
      console.warn('[auth] refreshProfile failed:', describeError(error));
    }
  }, [state.user, setState, fetchUserProfile]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize FCM after successful authentication
  useEffect(() => {
    if (!state.user || state.loading || fcmInitializedRef.current) return;

    const initFCM = async () => {
      fcmInitializedRef.current = true;

      // Delay to let the auth UI settle for smooth UX
      await new Promise((resolve) => {
        setTimeout(resolve, 1500);
      });

      if (fcm.permissionStatus === 'granted') {
        if (!fcm.isRegistered) {
          await fcm.initialize();
        } else {
          await fcm.linkDevice();
        }
      }
      // 'prompt' and 'denied' are handled by the permission dialog elsewhere
    };

    initFCM();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.user, state.loading]);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(() => {
    if (!state.user) {
      return {
        user: null,
        needsProfile: false,
        userStatus: null,
        userRole: null,
        checkUserSession,
        createUserProfile,
        refreshProfile,
        loading: status === 'loading',
        authenticated: status === 'authenticated',
        unauthenticated: status === 'unauthenticated',
        error: state.error,
      };
    }

    // Safely extract nested profile information
    const profileData = state.user?.profileData ?? {};
    const office = profileData.office ?? {};
    const history = profileData.history ?? {};

    return {
      user: {
        ...state.user,
        id: state.user?.uid,
        accessToken: state.user?.accessToken,
        displayName: state.user?.displayName ?? profileData.fullName,
        photoURL: state.user?.photoURL ?? profileData.photoURL,
        email: state.user?.email,
        phoneNumber: state.user?.phoneNumber,

        // Profile data
        fullName: profileData.fullName,
        dob: profileData.dob,
        office,
        history,

        // Credit information (for dealers)
        availableCredit: profileData.availableCredit ?? 0,

        // Status
        status: state.user?.status,
        needsProfile,
        userStatus,

        // Keep raw profileData for flexibility
        profileData: state.user?.profileData,
      },
      needsProfile,
      userStatus,
      userRole,
      checkUserSession,
      createUserProfile,
      refreshProfile,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      error: state.error,
    };
  }, [
    checkUserSession,
    createUserProfile,
    refreshProfile,
    state.user,
    state.error,
    status,
    needsProfile,
    userStatus,
    userRole,
  ]);

  return (
    <AuthContext value={memoizedValue}>
      {children}
      {notificationHandler.snackbar}
    </AuthContext>
  );
}
